// Menu tray / Play: two icon tabs in the header, Game Info and Completion,
// and Home as a button in a footer pinned below the scrolling entries.
// Game Info: How to Play (expandable), Score Detail (Apogee, expandable),
// Settings (Lettercase; Apogee also show star values). Full screen is a
// button in the play screen's top bar, beside the menu button.
// Completion, once the play is finished: The Sky
// is Written (the share image and buttons), Score and Leaderboard (High
// Score), Stats, Constellations. The menu opens on Completion once the play
// is finished and on Game Info before; completing a level opens it by itself.
import { scorePlay, type PlayLevel, type PlayState } from '@wits/core';
import type { ReactNode } from 'react';
import { homePath, navigate } from '../../app/router';
import type { PlayRecord } from '../../systems/api/types';
import type { MenuSection, MenuTab } from '../../systems/play/playSession';
import { playStats } from '../../systems/play/playStats';
import { quitPlaytest, restartPlaytest } from '../../systems/play/playtest';
import { gameSettings, setSetting, type Lettercase } from '../../systems/settings/gameSettings';
import { useStore } from '../../systems/store';
import { Button } from '../components/Button';
import { SettingButton } from '../components/SettingButton';
import { Toggle } from '../components/Toggle';
import { MenuTray, type MenuEntry } from '../menu/MenuTray';
import { ConstellationCards } from './completion/ConstellationCards';
import { ScoreTable } from './completion/ScoreTable';
import { SharePanel } from './completion/SharePanel';
import { StatsTable } from './completion/StatsTable';
import './completion/completion.css';

interface Props {
  open: boolean;
  tab: MenuTab;
  sections: MenuSection[];
  level: PlayLevel | null;
  board: PlayState | null;
  play: PlayRecord | null;
  extraWords: string[];
  /** A playtest from the level designer: Quit instead of Home, Reset under Debug, no sharing. */
  playtest: boolean;
  onClose: () => void;
  onSelectTab: (tab: MenuTab) => void;
  onToggleSection: (section: MenuSection) => void;
}

export function PlayMenu({ open, tab, sections, level, board, play, extraWords, playtest, onClose, onSelectTab, onToggleSection }: Props) {
  const complete = play?.status === 'finished';
  const apogee = level?.mode === 'apogee';
  const score = level && board && apogee ? scorePlay(level, board.constellations) : null;
  const final = complete ? (play?.score?.final ?? null) : null;
  const section = (key: MenuSection, label: string, content: ReactNode, detail?: string): MenuEntry => ({
    key,
    label,
    detail,
    content,
    open: sections.includes(key),
    onSelect: () => onToggleSection(key),
  });

  const entries: Array<MenuEntry | 'divider'> = [];
  if (tab === 'completion' && complete && level && board && play) {
    const stats = playStats(level, board, play, extraWords);
    if (!playtest) entries.push(section('sky', 'The Sky is Written', <SharePanel level={level} board={board} play={play} stats={stats} />));
    if (score) {
      entries.push(section('score', 'Score', <ScoreTable score={score} final={final ?? score.running} />, String(final ?? score.running)));
      entries.push({ key: 'leaderboard', label: 'Leaderboard', soon: true });
    }
    entries.push(section('stats', 'Stats', <StatsTable stats={stats} />));
    entries.push(
      section('constellations', 'Constellations', <ConstellationCards level={level} constellations={board.constellations} date={play.date} />, String(stats.constellations)),
    );
  } else {
    entries.push(section('how-to-play', 'How to Play', <HowToPlay level={level} />));
    if (score) entries.push(section('score-detail', 'Score Detail', <ScoreTable score={score} final={final} />));
    entries.push(section('settings', 'Settings', <Settings apogee={apogee} />));
    if (playtest) {
      entries.push('divider');
      entries.push(
        section(
          'debug',
          'Debug',
          <div className="settings">
            <Button
              variant="ghost"
              onClick={() => {
                onClose();
                restartPlaytest();
              }}
            >
              Reset the level
            </Button>
            <p className="quiet">Back to a fresh open: the board cleared, the clock restarted.</p>
          </div>,
        ),
      );
    }
  }

  return (
    <MenuTray
      open={open}
      title="Menu"
      tabs={[
        { key: 'info', label: 'Game info', icon: 'info' },
        { key: 'completion', label: complete ? 'Completion' : 'Completion, once the sky is written', icon: 'completion', disabled: !complete },
      ]}
      activeTab={tab}
      onSelectTab={(key) => onSelectTab(key as MenuTab)}
      entries={entries}
      onClose={onClose}
      footer={
        playtest ? (
          <Button variant="ghost" leadingIcon="home" onClick={quitPlaytest}>
            Quit
          </Button>
        ) : (
          <Button variant="ghost" leadingIcon="home" onClick={() => navigate(homePath())}>
            Home
          </Button>
        )
      }
    />
  );
}

const LETTERCASES: readonly Lettercase[] = ['upper', 'lower'];

function Settings({ apogee }: { apogee: boolean }) {
  const settings = useStore(gameSettings);
  return (
    <div className="settings">
      <SettingButton label="Lettercase" value={settings.lettercase} values={LETTERCASES} onChange={(c) => setSetting('lettercase', c)} />
      {apogee && <Toggle label="Show star values" checked={settings.showStarValues} onChange={(on) => setSetting('showStarValues', on)} />}
    </div>
  );
}

function HowToPlay({ level }: { level: PlayLevel | null }) {
  const min = level?.rules.minWordLength ?? 4;
  return (
    <div>
      <p>Tap lettered stars in spelling order. The game draws the lines; a constellation is the word kept on the sky.</p>
      <ul>
        <li>Lines never cross, and a line never passes over a star. A star under a line is blocked until the line goes.</li>
        <li>Every star is used once. Words need at least {min} letters.</li>
        <li>Tap the latest star to undo it, or an earlier one to rewind to it.</li>
      </ul>
      {level?.mode === 'puzzle' ? (
        <p>Only the words the sky was written for count. Tap a constellation to return it to the stars if it sits on the wrong ones.</p>
      ) : level?.mode === 'assemblage' ? (
        <p>
          Use every star. Any dictionary word counts, and the sky is written once no star is left over; a blocked star still counts as
          left over. Tap a constellation to return it to the stars and try another way.
        </p>
      ) : (
        <p>
          Any dictionary word scores: letter values, a bonus for spread-out shapes, double letters for a constellation that rings the
          planet. Tap a constellation to return it to the stars.
        </p>
      )}
    </div>
  );
}
